import React, { useEffect, useMemo } from 'react';
import { Alert, Card, Container, OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { connect } from 'react-redux';
import { isEqual, omit } from 'lodash';

import Option from './option_defaults/option';
import ManagedInput from 'components/form/managed_input';

import PagePicker from './page_picker';
import { setFormData } from './store/actions';
import { saveFormData } from './api';
import AvailabilityPicker from './availability_picker';
import CountryPicker from './country_picker';
import PropertyTypePicker from './property_type_picker';
import UserPicker from './user_picker';
import PanelHeader from './shared/panel_header';
import { faBuilding, faFile, faGlobe, faPaintBrush, faUsers, faWrench } from '@fortawesome/pro-solid-svg-icons';
import DefaultOptionMessage from './shared/default_option_message';
import Header from './shared/header';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type CorporateScopeType, type DocumentDefaultsType, type DocumentTemplateType } from './types';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import SaveDocumentTemplateModal from './shared/save_document_template_modal';
import BrandingSelector from './branding_selector';
import TagInput from './shared/tag_input';
import { Label } from '@buildoutinc/blueprint-react/ui/Label';

type DocumentTemplateFormProps = {
  afterSavePath: string,
  basePath: string,
  companyId?: number,
  companyName: string,
  corporateScope?: CorporateScopeType,
  formData: DocumentDefaultsType,
  parentCompanyDefaults?: DocumentDefaultsType,
  parentCompanyName?: string,
  setFormData: (formData: DocumentDefaultsType) => void,
  tagSuggestions: string[],
  template: DocumentTemplateType,
  teamsEnabled: boolean
};

type PageSettingsItem = {
  id: number,
  template_type?: string
};

type PageSettingsType = {
  availablePageTemplateTreeIds: PageSettingsItem[] | null,
  defaultPages: PageSettingsItem[] | null
};

export function DocumentTemplateForm({
  afterSavePath,
  basePath,
  companyId,
  companyName,
  corporateScope,
  formData,
  parentCompanyDefaults,
  parentCompanyName,
  tagSuggestions,
  teamsEnabled,
  template,
  setFormData
}: DocumentTemplateFormProps) {
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [tagsDirty, setTagsDirty] = React.useState(false);
  const [showPageTemplateModal, setShowPageTemplateModal] = React.useState(false);
  const [showCorporateOverrideModal, setShowCorporateOverrideModal] = React.useState(false);
  const initialPageSettings = React.useRef(extractPageSettings(formData));
  const initialDefaults = React.useRef(persistedDefaults(formData));
  const defaultsChanged = !isEqual(persistedDefaults(formData), initialDefaults.current);

  const parentCompanyBrandings = useMemo(() => {
    if (!parentCompanyDefaults) return [];

    const availableBrandings = parentCompanyDefaults.availableBrandings || [];
    const brandingIds = parentCompanyDefaults.brandingIds || [];
    if (brandingIds.length == 0) return availableBrandings;

    return availableBrandings.filter(b => brandingIds.includes(b.id));
  }, []);

  useEffect(() => {
    const onUnload = (event: BeforeUnloadEvent) => {
      if (dirty || tagsDirty) event.returnValue = true;
    };

    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty, tagsDirty]);

  const handleReturn = () => window.location.href = afterSavePath;

  // redirect after the render commits, so the save modal has finished closing first
  useEffect(() => {
    if (saved) handleReturn();
  }, [saved]);

  const save = (propagateToChildren = false) => {
    const wasDirty = dirty;
    const wasTagsDirty = tagsDirty;
    setDirty(false);
    setTagsDirty(false);

    // Sending the other defaults would give an inherited template its own copy of them, so it'd stop inheriting
    const putData: Partial<DocumentDefaultsType> = defaultsChanged
      ? { ...formData }
      : { tags: formData.tags || [] };

    if (companyId != null) putData.companyId = companyId;

    if (putData.defaultPages) {
      putData.defaultPages = putData.defaultPages.map(({ id, template_type }) => ({ id, template_type } as any));
    }
    if (putData.availablePageTemplateTreeIds) {
      putData.availablePageTemplateTreeIds = putData.availablePageTemplateTreeIds.map(({ id, template_type }) => (
        { id, template_type } as any
      ));
    }

    setSaving(true);
    saveFormData(basePath, putData, { propagateToChildren }).then(() => {
      setSaved(true);
      setShowPageTemplateModal(false);
    }).catch(() => {
      setSaving(false);
      if (wasDirty) setDirty(true);
      if (wasTagsDirty) setTagsDirty(true);
    });
  };

  const beforeSave = () => {
    if (
      template.hasChildTemplates &&
      pageSettingsChanged(initialPageSettings.current, extractPageSettings(formData))
    ) {
      setShowPageTemplateModal(true);
      return false;
    } else if (parentCompanyDefaults && defaultsChanged) {
      setShowCorporateOverrideModal(true);
      return false;
    }

    return true;
  };

  const handleSave = () => {
    if (beforeSave()) save(false);
  };

  const handleFormUpdate = (newFormData : DocumentDefaultsType) => {
    setDirty(true);
    setFormData(newFormData);
  };

  const updateName = (docName : string) => {
    handleFormUpdate({ ...formData, docName });
  };

  const updateTags = (tags: string[]) => {
    setTagsDirty(true);
    setFormData({ ...formData, tags });
  };

  const handleUpdatePropertyType = (
    { propertyTypes,
      propertySubtypes,
      selectAllPropertyTypes } : {
      propertyTypes?: number[],
      propertySubtypes?: number[],
      selectAllPropertyTypes?: boolean
    }
  ) => {
    const payload = { ...formData };
    if (selectAllPropertyTypes) {
      payload.selectAllPropertyTypes = true;
      payload.propertyTypes = null;
      payload.propertySubtypes = null;
    } else {
      payload.selectAllPropertyTypes = false;
      payload.propertyTypes = propertyTypes || null;
      payload.propertySubtypes = propertySubtypes || null;
    }

    handleFormUpdate(payload);
  };

  const handleUpdateOption = (changes) => {
    const docOptions = { ...formData.docOptions, ...changes };

    handleFormUpdate({ ...formData, docOptions });
  };

  const handleRemoveOptions = (names) => {
    const docOptions = { ...formData.docOptions };
    names.forEach((name) => {
      delete docOptions[name];
    });

    handleFormUpdate({ ...formData, docOptions });
  };

  const handleUserChange = ({ userIds, selectAll, teamIds }) => {
    const payload = { ...formData };
    if (selectAll) {
      payload.selectAllUserIds = true;
      payload.userIds = null;
      payload.teamIds = null;
    } else {
      payload.selectAllUserIds = false;
      if (userIds) payload.userIds = userIds;
      if (teamsEnabled && teamIds) payload.teamIds = teamIds;
    }

    handleFormUpdate(payload);
  };

  const handleUpdateAvailability = (
    { availability,
      enableForSaleAndLease } : {
      availability?: string[],
      enableForSaleAndLease?: boolean
    }
  ) => {
    const payload = { ...formData };
    if (enableForSaleAndLease) {
      payload.enableForSaleAndLease = true;
      payload.availability = 'sale_and_lease';
    } else {
      payload.enableForSaleAndLease = false;
      // Convert array to enum value
      if (availability && availability.length > 0) {
        if (availability.includes('sale') && availability.includes('lease')) {
          payload.availability = 'sale_and_lease';
        } else if (availability.includes('sale')) {
          payload.availability = 'sale';
        } else if (availability.includes('lease')) {
          payload.availability = 'lease';
        }
      } else {
        // When both are unchecked, set to null
        payload.availability = null;
      }
    }

    handleFormUpdate(payload);
  };

  const handleUpdateCountry = (
    { countryIds,
      selectAllCountryIds } : {
      countryIds?: number[],
      selectAllCountryIds?: boolean
    }
  ) => {
    const payload = { ...formData };
    if (selectAllCountryIds) {
      payload.selectAllCountryIds = true;
      payload.countryIds = null;
    } else {
      payload.selectAllCountryIds = false;
      payload.countryIds = countryIds || null;
    }

    handleFormUpdate(payload);
  };

  const options = template.options.map((option, i) => {
    return (
      <div className={i < template.options.length - 1 ? 'mb-3' : ''} key={option.name}>
        <Option
          defaults={formData.docOptions}
          option={option}
          onRemove={handleRemoveOptions}
          onUpdate={handleUpdateOption}
        />
      </div>
    );
  });

  const parentTemplateInfo = (
    <Alert className="mt-4" variant="info">
      <OverlayTrigger
        overlay={
          <Tooltip id="info-link-tooltip">
            {`
              Duplicated documents inherit their look & feel, margins, options,
              and other capabilities from the original document they are duplicated from.
              Click to view parent document.
            `}
          </Tooltip>
        }
        placement="bottom"
      >
        <a
          className="d-inline-flex align-items-center gap-1 text-decoration-none"
          href={template.parentTemplateEditPath}
          rel="noopener noreferrer"
          target="_blank"
        >
          <FontAwesomeIcon className="text-primary" icon={faCircleInfo} size="sm" />
          This document was duplicated. It inherits capabilities from{' '}
          <strong className="fw-semibold">{template.parentTemplateName}</strong>
        </a>
      </OverlayTrigger>
    </Alert>
  );

  const showBrandings = useMemo(() => {
    return parentCompanyBrandings.length > 0 || (formData.availableBrandings && formData.availableBrandings.length > 1);
  }, [parentCompanyBrandings, formData.availableBrandings]);

  return (
    <div>
      <Header
        modelName="Document Template"
        saving={saving}
        templateName={formData.docName || template.name}
        onCancel={handleReturn}
        onSave={handleSave}
      />
      <Container className="my-8">
        <Card className="p-5">
          <Card.Body>
            <div className="mb-7">
              <h6 className="my-4">Document Settings</h6>
              <div className="row">
                <div className="col-md-6">
                  <p className="fw-semibold my-2">Document Name</p>
                  <ManagedInput
                    className="form-control"
                    name="name-input"
                    placeholder={template.name}
                    value={formData.docName}
                    onChange={change => updateName(change['name-input'])}
                  />
                </div>
                <div className="col-md-6">
                  <Label className="fw-semibold my-2" htmlFor="document-template-tags">Document template tags</Label>
                  <TagInput
                    id="document-template-tags"
                    placeholder="Add a tag, like a team, agent, or version"
                    suggestions={tagSuggestions}
                    tags={formData.tags || []}
                    onChange={updateTags}
                  />
                  <p className="text-muted small mt-1 mb-0">Internal only.</p>
                </div>
              </div>
              {template.parentTemplateName && parentTemplateInfo}
              <h6 className="mt-7">Default Behavior</h6>
              <p className="my-2">
                Below, you are able to set the default behavior for this document template.
                You can choose which properties it is available for, which doc options it has,
                which users have access to it, and which pages appear by default.
                Any changes you make will be seen when a new version of this document is created.
              </p>
            </div>
            <Card className="mb-7">
              <PanelHeader
                icon={faBuilding}
                subtitle="Choose whether this document is available for sale or lease"
                title="Availability"
              />
              <Card.Body>
                <AvailabilityPicker
                  defaultAvailability={template.parameters.availability}
                  enableForSaleAndLease={formData.enableForSaleAndLease}
                  selectedAvailability={formData.availability}
                  onUpdateAvailability={handleUpdateAvailability}
                />
              </Card.Body>
            </Card>
            <Card className="mb-7">
              <PanelHeader
                icon={faGlobe}
                subtitle="Choose which countries this document is available for"
                title="Country"
              />
              <Card.Body>
                <CountryPicker
                  allowedCountryIds={corporateScope?.countryIds}
                  defaultCountryIds={template.parameters.countryIds}
                  selectAllCountryIds={formData.selectAllCountryIds}
                  selectedCountryIds={formData.countryIds}
                  onUpdateCountry={handleUpdateCountry}
                />
              </Card.Body>
            </Card>
            <Card className="mb-7" id="default-doc-property-types">
              <PanelHeader
                icon={faBuilding}
                subtitle="Choose which property types this document is available for"
                title="Property Type"
              />
              <Card.Body>
                <PropertyTypePicker
                  allowedPropertySubtypes={corporateScope?.propertySubtypes}
                  allowedPropertyTypes={corporateScope?.propertyTypes}
                  defaultPropertySubtypes={template.parameters.propertySubtypeIds}
                  defaultPropertyTypes={template.parameters.propertyTypeIds}
                  selectAllPropertyTypes={formData.selectAllPropertyTypes}
                  selectedPropertySubtypes={formData.propertySubtypes}
                  selectedPropertyTypes={formData.propertyTypes}
                  onUpdatePropertyType={handleUpdatePropertyType}
                />
              </Card.Body>
            </Card>
            <Card className="mb-7">
              <PanelHeader
                icon={faUsers}
                subtitle={`Choose which users ${teamsEnabled ? 'and teams ' : ''}are able to create this document`}
                title={`Users${teamsEnabled ? ' and Teams' : ''}`}
              />
              <Card.Body>
                <UserPicker
                  allowedTeamIds={corporateScope?.teamIds}
                  allowedUserIds={corporateScope?.userIds}
                  companyId={companyId}
                  defaultUserIds={template.parameters.userIds}
                  selectAll={formData.selectAllUserIds}
                  selectedTeamIds={formData.teamIds}
                  selectedUserIds={formData.userIds}
                  teamsEnabled={teamsEnabled}
                  onChange={handleUserChange}
                />
              </Card.Body>
            </Card>
            <div>
              {options.length > 0 && (
                <Card className="mb-7" id="default-doc-options">
                  <PanelHeader
                    icon={faWrench}
                    subtitle="Choose which document options are set by default when this document is created"
                    title="Document Options"
                  />
                  <Card.Body>
                    <DefaultOptionMessage/>
                    {options}
                  </Card.Body>
                </Card>
              )}
            </div>
            <Card className="mb-7">
              <PanelHeader
                icon={faFile}
                subtitle="Choose which pages populate by default in a new version of this document"
                title="Pages"
              />
              <Card.Body>
                <PagePicker
                  basePath={basePath}
                  defaultPages={formData.defaultPages ?? undefined}
                  marketOverviewSlotEnabled={template.marketOverviewSlotEnabled}
                  templateDefaultPageLibrary={template.defaultPageLibrary}
                  templateDefaultPages={template.defaultPages}
                  userCreated={template.userCreated}
                />
              </Card.Body>
            </Card>
            {showBrandings && (
              <Card className="mb-7">
                <PanelHeader
                  icon={faPaintBrush}
                  subtitle="Choose which brandings are available in this document"
                  title="Brandings"
                />
                <Card.Body>
                  <BrandingSelector parentCompanyBrandings={parentCompanyBrandings}/>
                </Card.Body>
              </Card>
            )}
            <SaveDocumentTemplateModal
              companyName={companyName}
              parentCompanyName={parentCompanyName}
              saving={saving}
              showCorporateOverrideModal={showCorporateOverrideModal}
              showPageTemplateModal={showPageTemplateModal}
              templateName={formData.docName || template.name}
              onClose={() => {
                setShowPageTemplateModal(false);
                setShowCorporateOverrideModal(false);
              }}
              onSave={save}
            />
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

function extractPageSettings(formData : DocumentDefaultsType) : PageSettingsType {
  return {
    availablePageTemplateTreeIds: normalizePages(formData.availablePageTemplateTreeIds),
    defaultPages: normalizePages(formData.defaultPages)
  };
}

function normalizePages(pages : DocumentDefaultsType['defaultPages']) {
  if (pages == null) return null;

  return pages.map(({ id, template_type }) => ({ id, template_type }));
}

// Page entries carry a client-only key that changes whenever a page is re-added
function persistedDefaults(formData : DocumentDefaultsType) {
  return { ...omit(formData, 'tags'), ...extractPageSettings(formData) };
}

function pageSettingsChanged(previousSettings : PageSettingsType, nextSettings : PageSettingsType) {
  return JSON.stringify(previousSettings) !== JSON.stringify(nextSettings);
}


const mapStateToProps = ({ formData }) => ({ formData });
const mapDispatchToProps = { setFormData };

export default connect(mapStateToProps, mapDispatchToProps)(DocumentTemplateForm);
